import { Path } from 'react-router-dom';
import { makeAutoObservable, toJS } from 'mobx';
import moment from 'moment';
import { CachedItem } from '@DStvMonoRepo/self-service-core';
import { environment } from '@environment';
import { FeatureRoutes } from 'core/constants/feature-routes';
import { Feature } from 'core/enums/features';
import { AbstractAuthService } from 'core/models/auth-service';
import { AuthConnectService } from 'core/services/auth-connect';
import { AuthSmartcardService } from 'core/services/auth-smartcard';
import { StorageHelper } from 'core/services/storage';
import { Util } from 'utils/common.utils';
import { AuthProvider, ConnectCustomer, ConnectStoreLoginResponse, Customer, DevUser, isConnectCustomer, isDevUser, MAHCustomer, UserAuth } from './auth-types';
import { AuthUtils, ConnectAuthType, DefaultUserAuth } from './auth-utils';


export class AuthStore {
  locale!: string;

  isValidating = false;
  isLoading = true;
  refreshTokenPointer!: NodeJS.Timeout;
  authService!: AbstractAuthService;

  user!: UserAuth;

  private readonly cachedUser = new CachedItem('user', true);
  private readonly cachedEntryUrl = new CachedItem('app-entry');

  appEntryUrl: Path = this.cachedEntryUrl.get();

  get country() {
    return this.user.customer.country;
  }

  get countryCode() {
    return isDevUser(this.user) ? this.user.countryOverride || this.user.customer.countryCode : this.user.customer.countryCode;
  }

  get customerNumber() {
    return isDevUser(this.user) ? this.user.customerOverride || this.user.customer.customerNumber as string : this.user.customer.customerNumber as string;
  }

  get emailAddress() {
    return isConnectCustomer(this.user.customer) ? this.user.customer.emailAddress : null;
  }

  get isAuthorized() {
    return this.user.isAuthorized;
  }

  get isAuthenticated() {
    return this.user.isAuthenticated && !!this.user.isLinked;
  }

  constructor() {
    makeAutoObservable(this);

    this.setUser();

    // Set auth provider based on user session- default to connect
    this.updateAuthService();
  }

  initialize(initialLocale: string, force = false) {
    this.changeLocale(initialLocale);

    if (this.isValidating) {
      return;
    }

    if (this.user.isAuthenticated && !force) {
      AuthUtils.CleanUrl();

      if (this.user.auth?.expiry) {

        if (this.shouldRefresh(this.user.auth.expiry)) {
          return this.refreshToken()
            .then(
              user => {
                this.changeLoadingState(false);

                return user || DefaultUserAuth;
              },
              err => {
                this.changeLoadingState(false);

                return DefaultUserAuth;
              }
            );
        }

        this.changeLoadingState(false);
        this.scheduleTokenRefresh(this.user.auth.expiry);

        return Promise.resolve(toJS(this.user));
      }

      this.changeLoadingState(false);
      // NOTE: this should probably fail if the user doesn't have an expiry, as it indicates a corrupt session.
      return Promise.resolve(toJS(this.user));
    }

    this.isValidating = true;

    this.setAppEntry();

    return this.authService
      .login(this.user)
      .then(
        (authenticationResponse: UserAuth) => {
          if (authenticationResponse.auth.expiry) {
            this.scheduleTokenRefresh(authenticationResponse.auth.expiry);
          }

          this.changeUserDetails(authenticationResponse);

          return toJS(this.user);
        },
        (err: any) => {
          console.error(`Sign-in failed`, err);

          return toJS(this.user);
        }
      )
      .finally(() => {
        this.changeLoadingState(false);
        this.isValidating = false;
      });
  }

  setUser(): UserAuth {
    // For new connect sessions check url for access token
    if (Util.CheckUrlForConnectHash(window.location.href)) {
      const params = Util.ParseConnectUrlHash();

      Util.RemoveConnectParams();

      // NOTE: catches cross-site mah auth
      if (params?.provider === AuthProvider.Mah) {
        return this.changeUser(AuthUtils.MapSmartcardSurnameResponse(params));
      }

      return this.changeUser(AuthUtils.MapConnectUserToUser(ConnectAuthType.UrlParameters, params));
    }

    // For standard login session check 'user' session- which will have the provider
    const cachedUser = this.cachedUser.get();
    if (!!cachedUser) {
      return this.changeUser(cachedUser);
    }

    // For legacy check 'connect-store' session
    const connectStore = AuthUtils.ConnectStore().get() as ConnectStoreLoginResponse;
    if (!!connectStore) {
      return this.changeUser(AuthUtils.MapConnectUserToUser(ConnectAuthType.LegacyConnectStore, connectStore));
    }

    // Else return empty user
    return this.changeUser({ ...DefaultUserAuth });
  }

  changeUser(user: UserAuth) {
    this.cacheUser(user);
    this.user = user;
    return this.user;
  }

  changeUserDetails(userPartial: Partial<DevUser | UserAuth>) {
    this.user = { ...this.user, ...userPartial };
    this.cacheUser(this.user);
  }

  changeCustomerDetails(customerPartial: Partial<Customer | ConnectCustomer | MAHCustomer>) {
    this.user.customer = { ...this.user.customer, ...customerPartial };
    this.cacheUser(this.user);
  }

  cacheUser(user: UserAuth) {
    if (!user.isDefault) {
      const { loginReq, ...userToCache } = user;
      this.cachedUser.set(userToCache);
    }
  }

  changeProvider(provider: AuthProvider) {
    if (this.user.isAuthorized) {
      console.error('Cannot change provider- user has active session.');
      return;
    }
    this.user.provider = provider;
    this.updateAuthService();
  }

  updateAuthService() {
    this.authService = (this.user.provider === AuthProvider.Mah) ? new AuthSmartcardService() : new AuthConnectService();
  }

  changeLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setAppEntry() {
    const paths = window.location.hash.replace('#', '').split('?');
    const pathname = paths[0]?.length > 1 ? paths[0] : `${FeatureRoutes[Feature.Dashboard]}`;
    const queryString = paths.length > 1 ? paths[1] : '';

    this.appEntryUrl = this.cachedEntryUrl.get() || { pathname, search: queryString }

    if (!this.cachedEntryUrl.get()) {
      this.cachedEntryUrl.set(this.appEntryUrl)
    }
  }

  login() {
    this.authService.signInUrl(
      `#/`, // NOTE: app re-entry redirect is handled by the landing page using app-entry
      `${this.locale || environment.DEFAULT_LANGUAGE + '-' + environment.DEFAULT_COUNTRY}`
    );
  }

  register() {
    this.authService.registerUrl(
      `#/`, // NOTE: app re-entry redirect is handled by the landing page using app-entry
      `${this.locale || environment.DEFAULT_LANGUAGE + '-' + environment.DEFAULT_COUNTRY}`
    );
  }

  clearCachedSession() {
    this.cachedUser.clear();
    AuthUtils.ConnectStore().clear();
    this.changeUser(DefaultUserAuth);
    this.cachedEntryUrl.clear();
    StorageHelper.GetInstance.removeFromStorage('req');
    StorageHelper.GetInstance.removeFromStorage('content');
    StorageHelper.GetInstance.removeFromStorage('actionReturn');
    StorageHelper.GetInstance.removeFromStorage('session');
    new CachedItem("on-error").clear();
    new CachedItem("app-entry").clear();
    this.updateAuthService();
  }

  logout() {
    this.clearCachedSession();
    this.authService.logout();
  }

  changeLocale(locale: string) {
    this.locale = locale;
  }

  shouldRefresh(expiry: number) {
    const expiresTime = moment
      .unix(expiry)
      .subtract(environment.REFRESH_TOKEN_TOLERANCE, 'minutes');

    return expiresTime.isSameOrBefore(moment());
  }

  private scheduleTokenRefresh(expiry: number) {
    if (!environment.USE_REFRESH_TOKEN || !this.authService.hasRefresh) {
      return;
    }

    if (this.refreshTokenPointer) {
      clearTimeout(this.refreshTokenPointer);
    }

    const expiresTime = moment
      .unix(expiry)
      .subtract(environment.REFRESH_TOKEN_TOLERANCE, 'minutes');
    const refreshTime = expiresTime.diff(moment(), 'milliseconds');

    if (isNaN(refreshTime)) {
      // NOTE: logic here is that this user's cached session may be corrupt
      this.logout();
      return;
    }

    this.refreshTokenPointer = setTimeout(() => {
      this.refreshToken();
    }, refreshTime);
  }

  //private
  refreshToken() {
    return this.authService.refreshToken(toJS(this.user))
      .then(
        (user) => {

          if (!user?.auth?.accessToken || !user.auth.expiry) {
            this.logout();
            return;
          }

          this.changeUserDetails(user);
          this.scheduleTokenRefresh(user.auth.expiry);

          return user;
        },
        err => {
          console.error('_Auth refresh failed -> ', err)
          this.logout();
          return;
        }
      );
  }
}